import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy – Belt Line Rd", address: "1305 E Belt Line Rd, Richardson, TX 75081", hours: "Daily 7 AM – 10 PM" },
  { name: "Walgreens – Arapaho Rd", address: "950 W Arapaho Rd, Richardson, TX 75080", hours: "Call for hours" },
  { name: "The UPS Store – Richardson", address: "990 S Greenville Ave, Richardson, TX 75081", hours: "Mon–Fri 8 AM – 6:30 PM" },
  { name: "CVS Pharmacy – Campbell Rd", address: "701 E Campbell Rd, Richardson, TX 75081", hours: "Daily 7 AM – 10 PM" },
  { name: "FedEx Office – Plano", address: "2050 W Spring Creek Pkwy, Plano, TX 75023", hours: "Call for hours" },
  { name: "Walgreens – Plano Pkwy", address: "1721 E Plano Pkwy, Plano, TX 75074", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Texas DPS – Richardson Driver License Office",
    address: "727 W Campbell Rd, Richardson, TX 75080",
    hours: "Mon–Fri 8:00 AM – 5:00 PM (closed Tue)",
    note: "Driver license issuance and REAL ID. Appointment strongly recommended — walk-in wait times can be 2–4 hours.",
    featured: true,
  },
  {
    name: "Texas DPS – Plano Driver License Office",
    address: "2701 Coit Rd, Plano, TX 75075",
    hours: "Mon–Fri 8:00 AM – 5:00 PM (closed Tue)",
    note: "Alternative DPS location for north Richardson residents. Similar services. Book online appointment.",
    featured: false,
  },
];

const vitalRecordsLinks = [
  {
    icon: "ri-user-smile-line",
    label: "Birth Certificate",
    desc: "Request certified birth certificates from the Texas Department of State Health Services (DSHS) Vital Statistics. Online, mail, or in-person options available.",
    url: "https://dshs.texas.gov/vs/",
  },
  {
    icon: "ri-file-list-2-line",
    label: "Death Certificate",
    desc: "Obtain certified death certificates through Texas DSHS Vital Statistics. Required for estate and legal matters.",
    url: "https://dshs.texas.gov/vs/",
  },
  {
    icon: "ri-hearts-line",
    label: "Marriage Certificate",
    desc: "Request marriage license records from the Dallas County Clerk. Certified copies available for name changes and legal purposes.",
    url: "https://www.dallascounty.org/departments/county-clerk/",
  },
  {
    icon: "ri-file-copy-line",
    label: "Divorce Decree",
    desc: "Obtain certified divorce records through the Dallas County District Clerk. Required for name change passport corrections and legal documentation.",
    url: "https://www.dallascounty.org/departments/district-clerk/",
  },
];

export default function RichardsonHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Richardson Travelers</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Here are local North Dallas County resources to help you gather everything you need before your next international trip.</p>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0"><i className="ri-camera-line text-white text-lg"></i></div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Richardson</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular locations near Richardson:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button onClick={() => setPhotoExpanded(true)} className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors">
                  <i className="ri-add-circle-line text-sm"></i>Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed"><i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i><strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.</p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Dallas County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records serving Richardson residents. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {vitalRecordsLinks.map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Dallas County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0"><i className="ri-id-card-line text-white text-lg"></i></div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Texas DPS — Richardson &amp; Plano Offices</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">A valid government-issued photo ID is required for your passport application. Richardson residents can obtain or renew their Texas driver&apos;s license or REAL ID at these DPS offices.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                    <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                    <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0"><i className="ri-flight-takeoff-line text-white text-lg"></i></div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Richardson Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { code: "DFW", name: "Dallas/Fort Worth International Airport", distance: "About 20 miles west — 30–40 min from Richardson", address: "2400 Aviation Drive, DFW Airport, TX 75261", note: "Primary international gateway. World-class connectivity to Europe, Asia, Latin America. Most Richardson travelers depart here for international trips.", featured: true },
                  { code: "DAL", name: "Dallas Love Field Airport", distance: "About 22 miles southwest", address: "8008 Herb Kelleher Way, Dallas, TX 75235", note: "Southwest Airlines hub. Primarily domestic service — most international travelers use DFW instead.", featured: false },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary International</span>}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2"><i className="ri-road-map-line"></i><span>{airport.distance}</span></div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed">{airport.note}</p>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international DFW flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}